import type { CSSProperties } from 'react';
import { secuenciaOf, type ResumenSecuencia, type Snapshot } from '../lib/snapshot-types.js';
import { porcentaje, unDecimal } from '../lib/etiquetas-metricas.js';
import { hashDeRuta } from '../lib/hash-route.js';

/**
 * LA SECUENCIA EN EL PANEL (16-oct). Joan, 15-oct: quiere VER el tiempo trabajando cada deal, los
 * impactos y el % de la secuencia por el que va cada uno. Una fila, debajo de reuniones y embudo:
 *
 *   - Tres anillos, cada uno una TASA con su numerador y su base escritos debajo: el avance (ciclos
 *     hechos de los que caben), las llamadas al dia (las de dentro sin retraso) y quien respondio
 *     (de las que entraron en 90 dias). Un anillo sin base no se pinta (nunca un «sin datos»).
 *   - Una columna de cifras: los impactos (toques hechos, por canal y por persona) y el tiempo en el
 *     deal (medianas de dias de las de dentro).
 *
 * Es lo de AHORA (lo dice: «· ahora»), como el embudo. Las cuentas son las del servidor
 * (secuencia-resumen.ts), aqui no se rehace ninguna. Cada anillo y el titulo llevan a
 * Seguimiento > Secuencia. Solo donde el servidor manda el bloque (la secuencia encendida).
 */

const ENLACE = hashDeRuta({ kind: 'seguimiento', vista: 'secuencia' });

export interface AnilloSecuencia {
  clave: 'avance' | 'al_dia' | 'respondieron';
  titulo: string;
  /** Numerador y base de la tasa (la base nunca es 0: sin base, no hay anillo). */
  n: number;
  base: number;
  /** La tasa en texto («12 %»). */
  valor: string;
  /** «x de y ...», debajo del titulo. */
  cifra: string;
  detalle: string | null;
  /** El detalle pide atencion (llamadas con retraso). */
  aviso: boolean;
  /** La frase entera para el lector de pantalla. */
  frase: string;
}

const dias = (n: number): string => `${Number.isInteger(n) ? String(n) : unDecimal(n)} ${n === 1 ? 'día' : 'días'}`;
const personas = (n: number): string => `${n} ${n === 1 ? 'persona' : 'personas'}`;

/**
 * La tasa de un anillo: sin decimales, salvo si redondear diria 0 % o 100 % sin serlo (1 de 250
 * no es «0 %»): entonces con uno.
 */
export function tasaAnillo(n: number, base: number): string {
  const x = (100 * n) / base;
  const redondo = Math.round(x);
  return (redondo === 0 && n > 0) || (redondo === 100 && n < base) ? porcentaje(x, 1) : porcentaje(x, 0);
}

/** Por canal, solo los que tienen alguna: «1 por teléfono · 2 por correo». */
function porCanal(t: ResumenSecuencia['cifras']['respondieron']['porCanal']): string | null {
  const partes = [
    t.llamada > 0 && `${t.llamada} por teléfono`,
    t.email > 0 && `${t.email} por correo`,
    t.whatsapp > 0 && `${t.whatsapp} por WhatsApp`,
  ].filter((x): x is string => !!x);
  return partes.length ? partes.join(' · ') : null;
}

/** Los anillos que tienen base, en su orden. */
export function anillosDeSecuencia(r: ResumenSecuencia): AnilloSecuencia[] {
  const c = r.cifras;
  const out: AnilloSecuencia[] = [];
  if (c.enSecuencia > 0) {
    const base = r.ciclos * c.enSecuencia;
    const valor = tasaAnillo(r.ciclosHechos, base);
    const media = unDecimal(r.ciclosHechos / c.enSecuencia);
    out.push({
      clave: 'avance', titulo: 'Avance de la secuencia', n: r.ciclosHechos, base, valor,
      cifra: `${r.ciclosHechos} de ${base} ciclos`,
      detalle: `${media} de ${r.ciclos} de media · ${c.enSecuencia} dentro`,
      aviso: false,
      frase: `Avance de la secuencia: ${valor}. ${r.ciclosHechos} de ${base} ciclos hechos, ${media} de ${r.ciclos} de media entre ${personas(c.enSecuencia)} dentro.`,
    });
    const alDia = c.enSecuencia - c.esperan.conRetraso;
    const e = c.esperan;
    const quienes = e.n === 0 ? 'Nadie espera tu llamada'
      : `${e.n} ${e.n === 1 ? 'espera' : 'esperan'} tu llamada${e.conRetraso > 0 && e.conRetraso < e.n ? `, ${e.conRetraso} con retraso` : ''}`;
    const cola = e.n === 0 ? null : e.conRetraso === 0 ? 'ninguna con retraso' : e.masAntigua !== null ? `la más antigua, ${dias(e.masAntigua)}` : null;
    const valorDia = tasaAnillo(alDia, c.enSecuencia);
    out.push({
      clave: 'al_dia', titulo: 'Llamadas al día', n: alDia, base: c.enSecuencia, valor: valorDia,
      cifra: `${alDia} de ${c.enSecuencia} sin retraso`,
      detalle: cola ? `${quienes} · ${cola}` : quienes,
      aviso: e.conRetraso > 0,
      frase: `Llamadas al día: ${valorDia}. ${alDia} de ${personas(c.enSecuencia)} dentro sin retraso. ${quienes}${cola ? `; ${cola}` : ''}.`,
    });
  }
  if (c.entraron > 0) {
    const valor = tasaAnillo(c.respondieron.n, c.entraron);
    const canales = porCanal(c.respondieron.porCanal);
    out.push({
      clave: 'respondieron', titulo: 'Respondieron', n: c.respondieron.n, base: c.entraron, valor,
      cifra: `${c.respondieron.n} de ${c.entraron} en ${r.ventanaDias} días`,
      detalle: canales,
      aviso: false,
      frase: `Respondieron: ${valor}. ${c.respondieron.n} de ${personas(c.entraron)} que entraron en ${r.ventanaDias} días${canales ? `: ${canales.split(' · ').join(', ')}` : ''}.`,
    });
  }
  return out;
}

function Anillo({ a, orden }: { a: AnilloSecuencia; orden: number }) {
  const parte = Math.min(100, Math.max(0, (100 * a.n) / a.base));
  return (
    <figure className={`sec-anillo sec-anillo--${a.clave}`} aria-label={a.frase} style={{ '--sec-retraso': `${orden * 80}ms` } as CSSProperties}>
      <div className="sec-anillo-dibujo">
        <svg viewBox="0 0 120 120" aria-hidden="true" focusable="false">
          <circle className="sec-anillo-pista" cx="60" cy="60" r="50" fill="none" strokeWidth="10" />
          {parte > 0 && <circle className="sec-anillo-trazo" cx="60" cy="60" r="50" fill="none" strokeWidth="10" pathLength="100" strokeDasharray={`${parte} 100`} transform="rotate(-90 60 60)" />}
        </svg>
        <strong className="sec-anillo-valor">{a.valor}</strong>
      </div>
      <figcaption className="sec-anillo-texto">
        <a className="sec-anillo-titulo" href={ENLACE}>{a.titulo}</a>
        <span className="sec-anillo-n">{a.cifra}</span>
        {a.detalle && <span className={`sec-anillo-detalle${a.aviso ? ' sec-anillo-detalle--aviso' : ''}`}>{a.detalle}</span>}
      </figcaption>
    </figure>
  );
}

/** Los impactos y el tiempo en el deal: cifras, no tasas (sin anillo). */
function Cifras({ r }: { r: ResumenSecuencia }) {
  const t = r.cifras.toques;
  const total = t.llamada + t.email + t.whatsapp;
  const parcial = r.conDeal < r.cifras.enSecuencia ? ` (${r.conDeal} de ${r.cifras.enSecuencia} con oportunidad)` : '';
  return (
    <div className="sec-cifras">
      <div className="sec-cifra">
        <span className="sec-cifra-rotulo">Impactos</span>
        <strong className="sec-cifra-valor">{total}</strong>
        <span className="sec-cifra-pie">
          {t.llamada} {t.llamada === 1 ? 'llamada' : 'llamadas'} · {t.email} {t.email === 1 ? 'correo' : 'correos'} · {t.whatsapp} WhatsApp
        </span>
        {r.toquesPorPersona !== null && <span className="sec-cifra-pie">{unDecimal(r.toquesPorPersona)} por persona en {r.ventanaDias} días</span>}
      </div>
      {(r.medianaDiasDeal !== null || r.medianaDiasSecuencia !== null) && (
        <div className="sec-cifra">
          <span className="sec-cifra-rotulo">Tiempo en el deal <span>· mediana</span></span>
          {r.medianaDiasDeal !== null && <span className="sec-cifra-linea"><strong>{dias(r.medianaDiasDeal)}</strong> desde que se creó la oportunidad{parcial}</span>}
          {r.medianaDiasSecuencia !== null && <span className="sec-cifra-linea"><strong>{dias(r.medianaDiasSecuencia)}</strong> dentro de la secuencia</span>}
        </div>
      )}
    </div>
  );
}

export function SecuenciaAnillos({ snapshot }: { snapshot: Snapshot }) {
  const r = secuenciaOf(snapshot);
  if (!r) return null;
  const anillos = anillosDeSecuencia(r);
  return (
    <section className="section sec" aria-label="Secuencia">
      <div className="seccion-cabeza">
        <h2 className="section-title"><a className="sec-titulo" href={ENLACE}>Secuencia</a><span className="seccion-periodo"> · ahora</span></h2>
      </div>
      {r.cifras.entraron === 0 ? (
        <p className="section-empty">Nadie ha entrado en la secuencia en {r.ventanaDias} días.</p>
      ) : (
        <div className="sec-fila" style={{ '--sec-columnas': anillos.length + 1 } as CSSProperties}>
          {anillos.map((a, i) => <Anillo key={a.clave} a={a} orden={i} />)}
          <Cifras r={r} />
        </div>
      )}
    </section>
  );
}
